/**
 * React Query hooks for admin user purge and user-workspace lookup.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminGetUserWorkspaces,
  adminPurgeUser,
  type AdminUserWorkspacesResponse,
  type AdminPurgeUserResponse,
} from "../services/naraDoor";

/**
 * Fetch the workspaces a given user belongs to (admin view).
 * Enabled only when userId is provided.
 */
export function useAdminUserWorkspaces(userId: string | undefined) {
  return useQuery<AdminUserWorkspacesResponse>({
    queryKey: ["adminUserWorkspaces", userId],
    queryFn: () => {
      if (!userId) throw new Error("userId is required");
      return adminGetUserWorkspaces(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Mutation to permanently purge a user and their sole workspace.
 * On success, invalidates user list and workspace list caches.
 */
export function useAdminPurgeUser() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminPurgeUserResponse,
    Error,
    { userId: string; workspaceId?: string | null; confirmEmail: string }
  >({
    mutationFn: ({ userId, workspaceId, confirmEmail }) =>
      adminPurgeUser({ userId, workspaceId, confirmEmail }),
    onSuccess: (_, variables) => {
      // Invalidate caches for the purged user, their workspace, and list queries
      queryClient.invalidateQueries({ queryKey: ["adminUser", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminUserWorkspaces", variables.userId] });
      if (variables.workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["adminWorkspaceDetail", variables.workspaceId] });
      }
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaces"] });
    },
  });
}
